/**
 * TEMPORARY per-clinic overrides, for previewing the redesign on staging.
 *
 * Everything here exists because a clinic's Contentful entry is wrong or
 * incomplete. Each one should be retired by fixing the entry, not by keeping
 * this file forever. Images are referenced by an asset title, or by part of a
 * file name where two assets share a title, so nothing here invents content;
 * it only points at assets that already exist.
 *
 * Fields, all optional:
 *   heroImage       dev asset to use for the hero, replacing the entry's own
 *   gallery         dev assets to use for the Why Choose carousel
 *   servicesPhoto   dev asset for the services section, so it differs from the
 *                   hero
 *   excludeTeam     people to drop from the team section
 *   prodHero        production photo for the hero, see below
 *   prodGallery     production photos for the Why Choose carousel
 *   prodTeam        production staff, replacing the section's own list
 *
 * The prod* fields exist because dev and production Contentful hold different
 * content and are never synced. Several centres have full photo sets and
 * correct staff in production while dev still has a logo, a placeholder, or
 * the wrong people. Those are pinned here by URL so the staging preview shows
 * what the redesign will actually look like once it ships.
 *
 * Nothing is invented: every URL is a public Contentful file that production
 * already serves today. Delete the prod* fields before this reaches
 * production, where the entries are already right.
 */

/**
 * Generic Cortica photos, used where a centre has no usable photo of its own.
 * Every one is a real Contentful asset; none is invented. They are spread
 * around so no two neighbouring centres show the same placeholder.
 *
 * Retire each as soon as that centre's own photos are uploaded.
 */
const STOCK_THERAPY = "Kids playing with toys on a green mat"
const STOCK_SPEECH = "A child in speech language therapy at Cortica"
const STOCK_SENSORY =
  "children participating in a yoga class for sensorimotor play treatment"
const STOCK_STRETCH = "Children stretching"
const STOCK_READING = "Reading to the class of young children"
const STOCK_ABA = "Applied Behavior Analysis"
const STOCK_BLOCKS = "Children with blocks"
const STOCK_IPAD = "Children at table with iPad"
const STOCK_DEV = "Developmental Therapies"
const STOCK_WOMAN_BLOCKS = "Woman and child with blocks"
// Two assets share the title "Woman with child", so this one is picked by
// file name. The other is a Getty stock photo of a different pair.
const STOCK_WOMAN_CHILD = "woman_and_child_smile"
const STOCK_DEV_CORTICA = "Developmental Therapies at Cortica"

/**
 * Hero photo of last resort, for a centre with no image and no gallery at all.
 * Centres that need one are given a specific photo below so they do not all
 * look alike; this catches any centre created later.
 */
const DEFAULT_STOCK_HERO = STOCK_THERAPY

export const stockHeroFor = () => DEFAULT_STOCK_HERO

const CDN = "//images.ctfassets.net/67wivm6jm6qo"
const CAROUSEL_WIDTHS = [480, 720, 962, 1444]

/**
 * Build the shape a Contentful asset node has, so a pinned production photo
 * can be dropped anywhere the components already accept one.
 */
const prodPhoto = ([title, path, aspectRatio = 1.5]) => {
  const url = `${CDN}/${path}`

  return {
    file: { url },
    fluid: {
      aspectRatio,
      sizes: "(max-width: 962px) 100vw, 962px",
      src: `${url}?w=962&q=90&fm=webp`,
      srcSet: CAROUSEL_WIDTHS.map(
        width => `${url}?w=${width}&q=90&fm=webp ${width}w`
      ).join(", "),
    },
    title,
  }
}

const richText = paragraphs => ({
  content: paragraphs.map(text => ({
    content: [{ data: {}, marks: [], nodeType: "text", value: text }],
    data: {},
    nodeType: "paragraph",
  })),
  data: {},
  nodeType: "document",
})

/** Build the shape a staff member has, from their production record. */
const prodPerson = ({ bio, name, photo, role }) => ({
  details: bio ? { json: richText(bio) } : null,
  name,
  picture: { fluid: { src: `${CDN}/${photo}?w=200&h=200&q=90` } },
  role,
  slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
})

// The Trumbull shoot, five photos, none of which reached dev.
const TRUMBULL_PHOTOS = [
  [
    "Trumbull 1",
    "GBH1tJks04RvuFlSCBvYR/4cbbd09982e2254baecf649a0469d4c1/028_HDR_3315.jpg",
    1.5,
  ],
  [
    "Trumbull 2",
    "1tskYEe3sFa0k2GfbfWrNn/7b0652118f16af3e5973f21ca039090d/037_HDR_3401.jpg",
    1.5,
  ],
  [
    "Trumbull 3",
    "17mcGRDp79mqOCrpsyU9yM/20ad44d69b1b43b83ba4e0e48cde9b4d/029_HDR_3301.jpg",
    1.5,
  ],
  [
    "Trumbull 4",
    "uQmK0Pe4ZtJstyxJQBVZc/842916c3dd99ac91e7ecb2b4b0512a2a/022_HDR_3347.jpg",
    1.5,
  ],
  [
    "Trumbull 5",
    "v7tUTbIA0KV6WFjmLtR2R/ee2bc24c2821ec0c93fdb3bf691e792c/DJI_0995-Edit.jpg",
    1.78,
  ],
].map(prodPhoto)

const CLINIC_OVERRIDES = {
  // Entry's hero is "Cortica x Melmed", a logo rather than a photo, and there
  // is no gallery.
  "az/scottsdale": {
    gallery: [STOCK_ABA, STOCK_IPAD, STOCK_WOMAN_BLOCKS],
    heroImage: STOCK_READING,
    servicesPhoto: STOCK_BLOCKS,
  },

  // Entry's hero is "Carlsbad 1", a photo of a different centre, and there is
  // no gallery.
  "ca/concord": {
    gallery: [STOCK_STRETCH, STOCK_BLOCKS, STOCK_DEV],
    heroImage: STOCK_STRETCH,
    servicesPhoto: STOCK_WOMAN_BLOCKS,
  },

  // Entry's hero is "Dedham-17", a photo of a different centre, and there is
  // no gallery.
  "ca/inland-empire": {
    gallery: [STOCK_SPEECH, STOCK_THERAPY, STOCK_BLOCKS],
    heroImage: STOCK_DEV,
    servicesPhoto: STOCK_SENSORY,
  },

  // Same, and the entry has no gallery to draw a services photo from either.
  "ca/westlake-village": {
    gallery: [STOCK_READING, STOCK_WOMAN_BLOCKS, STOCK_ABA],
    servicesPhoto: STOCK_STRETCH,
  },

  // Hero and services were both showing an asset titled "warren" that is
  // actually a photo of the Laguna Niguel centre. No Warren photos exist.
  "nj/warren": {
    gallery: [STOCK_STRETCH, STOCK_DEV, STOCK_WOMAN_BLOCKS],
    heroImage: STOCK_BLOCKS,
    servicesPhoto: STOCK_READING,
  },

  // Entry's hero is "Westchester Exterior", a photo of a different centre.
  "tx/the-woodlands": {
    gallery: [STOCK_READING, STOCK_BLOCKS, STOCK_IPAD],
    heroImage: STOCK_ABA,
    servicesPhoto: STOCK_THERAPY,
  },

  // No image and no gallery on the entry. Stock stands in until real photos
  // of these centres are uploaded, varied so the four do not look identical.
  "east-mesa-az-autism-therapy": {
    gallery: [STOCK_SPEECH, STOCK_DEV, STOCK_WOMAN_BLOCKS],
    heroImage: STOCK_THERAPY,
    servicesPhoto: STOCK_BLOCKS,
  },
  "glendale-west-phoenix-az-autism-therapy": {
    gallery: [STOCK_ABA, STOCK_STRETCH, STOCK_IPAD],
    heroImage: STOCK_WOMAN_CHILD,
    servicesPhoto: STOCK_DEV,
  },
  "tempe-az-autism-therapy": {
    gallery: [STOCK_READING, STOCK_WOMAN_BLOCKS, STOCK_THERAPY],
    heroImage: STOCK_DEV_CORTICA,
    servicesPhoto: STOCK_STRETCH,
  },

  // Dedham's own photos were uploaded to dev in August 2026, so the entry
  // drives the hero and carousel now. Only the team still differs from the
  // live site.
  "ma/dedham": {
    prodTeam: [
      {
        name: "Alesia Sheridan",
        photo:
          "aj5KQErcTzMxGzTj86TpV/e56fdea7ed80a4b7eed1ddc473a06513/Alesia_Sheridan.jpeg",
        role: "Site Director",
      },
      {
        bio: [
          "Nicole Mayo is the Developmental Therapies Site Supervisor for Shrewsbury, MA, West Springfield MA, Dedham MA and Quincy MA. She has 4+ years of supervisory experience and 12+ years of experience working as an occupational therapist.",
          "She thrives on supporting clinicians and providing whole-child care to each individual in order for them to gain greater independence within their daily routine activities. She lives in Western Mass and enjoys spending quality time with her husband and 3 children.",
        ],
        name: "Nicole Mayo",
        photo:
          "X8wzjgZFaKs0zO4qSOjNl/385fbf99c557cc3fb4416b99132732e5/nicole-mayo-bio.png",
        role: "Developmental Therapies Site Supervisor",
      },
    ].map(prodPerson),
  },

  // Entry's hero is the Cortica/Springtide logo file rather than a photo, and
  // the centre has no gallery. One real Shrewsbury photo exists, which takes
  // the hero; the carousel and services photo pad with generic Cortica stock.
  "ma/shrewsbury": {
    gallery: ["Shrewsbury 1", STOCK_THERAPY, STOCK_SPEECH],
    heroImage: "Shrewsbury 1",
    servicesPhoto: STOCK_ABA,
  },

  // Dev has the Springtide logo as the hero and no gallery. Production has
  // the full five-photo Trumbull shoot, so no stock is needed here.
  "ct/trumbull": {
    prodGallery: TRUMBULL_PHOTOS,
    prodHero: TRUMBULL_PHOTOS[0],
    servicesPhoto: STOCK_IPAD,
  },

  // Entry's hero is a stock image titled "blog". The centre's own gallery
  // already contains a real photo of the building.
  "ca/san-diego": {
    excludeTeam: ["Shanel Ginyard", "Paige Funderburk"],
    heroImage: "Cortica San Diego",
  },

  // Hero and services section were both showing "Plano 3". Two Contentful
  // assets are titled "Plano 1", so the music-therapy room is picked by file
  // name; the other is a small portrait shot of the building exterior.
  "tx/plano": {
    excludeTeam: ["Theresa Mutzig-Erwin", "Sailaja Golla"],
    servicesPhoto: "K85_6560",
  },
}

/**
 * "Nearby Areas We Serve" for every centre, taken from the live site.
 *
 * Production carries this list on all 28 clinic pages; dev carries it on none
 * of the older 25, so the accordion never appeared on staging. These are the
 * live lists, matched to each dev clinic by name.
 *
 * A clinic's own Contentful content always wins. This is only a stand-in for
 * the centres where dev has nothing, and should be deleted once dev is topped
 * up from master.
 */
const NEARBY_AREAS = {
  scottsdale: [
    "Phoenix, AZ",
    "Tempe, AZ",
    "Mesa, AZ",
    "Chandler, AZ",
    "Gilbert, AZ",
    "Paradise Valley, AZ",
    "Fountain Hills, AZ",
    "Scottsdale Ranch, AZ",
    "Ahwatukee, AZ",
    "North Scottsdale, AZ",
  ],
  burlington: [
    "Woburn, MA",
    "Lexington, MA",
    "Bedford, MA",
    "Billerica, MA",
    "Wilmington, MA",
    "Reading, MA",
    "Stoneham, MA",
    "Winchester, MA",
    "Arlington, MA",
    "Medford, MA",
  ],
  carlsbad: [
    "Oceanside, CA",
    "Encinitas, CA",
    "Vista, CA",
    "San Marcos, CA",
    "Leucadia, CA",
    "Rancho Santa Fe, CA",
    "La Costa, CA",
    "Del Mar, CA",
    "Poway, CA",
    "Escondido, CA",
  ],
  concord: [
    "Walnut Creek, CA",
    "Pleasant Hill, CA",
    "Martinez, CA",
    "Oakley, CA",
    "Pittsburg, CA",
    "Antioch, CA",
    "Brentwood, CA",
    "Clayton, CA",
    "Lafayette, CA",
    "Danville, CA",
  ],
  glendale: [
    "Burbank, CA",
    "Pasadena, CA",
    "North Hollywood, CA",
    "Toluca Lake, CA",
    "La Crescenta, CA",
    "Montrose, CA",
    "Sunland, CA",
    "Tujunga, CA",
    "Studio City, CA",
    "Sherman Oaks, CA",
  ],
  irvine: [
    "Tustin, CA",
    "Lake Forest, CA",
    "Mission Viejo, CA",
    "Newport Beach, CA",
    "Costa Mesa, CA",
    "Huntington Beach, CA",
    "Laguna Beach, CA",
    "Aliso Viejo, CA",
    "Rancho Santa Margarita, CA",
    "San Clemente, CA",
  ],
  "laguna-niguel": [
    "Mission Viejo, CA",
    "Dana Point, CA",
    "San Juan Capistrano, CA",
    "Laguna Beach, CA",
    "Aliso Viejo, CA",
    "Irvine, CA",
    "Lake Forest, CA",
    "Rancho Santa Margarita, CA",
    "Coto de Caza, CA",
    "San Clemente, CA",
  ],
  marin: [
    "San Rafael, CA",
    "Sausalito, CA",
    "Mill Valley, CA",
    "Larkspur, CA",
    "Novato, CA",
    "Tiburon, CA",
    "Ross, CA",
    "Belvedere, CA",
    "Fairfax, CA",
    "San Anselmo, CA",
  ],
  "san-diego": [
    "La Jolla, CA",
    "Pacific Beach, CA",
    "Mission Valley, CA",
    "Clairemont, CA",
    "Kearny Mesa, CA",
    "Chula Vista, CA",
    "El Cajon, CA",
    "Encinitas, CA",
    "Del Mar, CA",
    "Poway, CA",
  ],
  torrance: [
    "Redondo Beach, CA",
    "Manhattan Beach, CA",
    "Hermosa Beach, CA",
    "Palos Verdes Estates, CA",
    "Rolling Hills, CA",
    "San Pedro, CA",
    "Rancho Palos Verdes, CA",
    "El Segundo, CA",
    "Lawndale, CA",
    "Gardena, CA",
  ],
  "westlake-village": [
    "Thousand Oaks, CA",
    "Agoura Hills, CA",
    "Oak Park, CA",
    "Calabasas, CA",
    "Newbury Park, CA",
    "Woodland Hills, CA",
    "Simi Valley, CA",
    "Moorpark, CA",
    "Camarillo, CA",
    "Malibu, CA",
  ],
  cary: [
    "Raleigh, NC",
    "Apex, NC",
    "Fuquay-Varina, NC",
    "Holly Springs, NC",
    "Morrisville, NC",
    "Garner, NC",
    "Wake Forest, NC",
    "Chapel Hill, NC",
    "Durham, NC",
    "Cumberland, NC",
  ],
  charlotte: [
    "Matthews, NC",
    "Huntersville, NC",
    "Concord, NC",
    "Gastonia, NC",
    "Mint Hill, NC",
    "Monroe, NC",
    "Cornelius, NC",
    "Davidson, NC",
    "Waxhaw, NC",
  ],
  trumbull: [
    "Fairfield, CT",
    "Bridgeport, CT",
    "Milford, CT",
    "Stratford, CT",
    "Monroe, CT",
    "Shelton, CT",
    "Westport, CT",
    "Norwalk, CT",
    "Danbury, CT",
    "Greenwich, CT",
  ],
  "east-mesa": ["Mesa", "Gilbert"],
  "glendale-west-phoenix": [
    "Phoenix, AZ",
    "Glendale, AZ",
    "Peoria, AZ",
    "Avondale, AZ",
    "Goodyear, AZ",
    "Tolleson, AZ",
  ],
  westchester: [
    "La Grange, IL",
    "Oak Brook, IL",
    "Elmhurst, IL",
    "Oak Park, IL",
    "Western Springs, IL",
    "Riverside, IL",
    "Berwyn, IL",
    "Hinsdale, IL",
    "Broadview, IL",
    "Bellwood, IL",
  ],
  braintree: [
    "Quincy, MA",
    "Brookline, MA",
    "Weymouth, MA",
    "Milton, MA",
    "Dedham, MA",
    "Needham, MA",
    "Canton, MA",
    "Randolph, MA",
    "Holbrook, MA",
    "Norwood, MA",
  ],
  danvers: [
    "Salem, MA",
    "Peabody, MA",
    "Beverly, MA",
    "Lynn, MA",
    "Marblehead, MA",
    "Swampscott, MA",
    "Gloucester, MA",
    "Danversport, MA",
    "Gloucester, MA",
    "Wakefield, MA",
  ],
  dedham: [
    "Norwood, MA",
    "Westwood, MA",
    "Milton, MA",
    "Quincy, MA",
    "Randolph, MA",
    "Canton, MA",
    "Needham, MA",
    "Brookline, MA",
    "Weymouth, MA",
    "Boston, MA",
  ],
  shrewsbury: [
    "Worcester, MA",
    "Westborough, MA",
    "Northborough, MA",
    "Grafton, MA",
    "Auburn, MA",
    "Marlborough, MA",
    "Southborough, MA",
    "Holden, MA",
    "Leicester, MA",
    "Hopkinton, MA",
  ],
  "west-springfield": [
    "Springfield, MA",
    "Holyoke, MA",
    "Agawam, MA",
    "Chicopee, MA",
    "Westfield, MA",
    "East Longmeadow, MA",
    "Southwick, MA",
    "Feeding Hills, MA",
    "Granville, MA",
    "Indian Orchard, MA",
  ],
  naperville: [
    "Wheaton, IL",
    "Aurora, IL",
    "Lisle, IL",
    "Downers Grove, IL",
    "Bolingbrook, IL",
    "Plainfield, IL",
    "Geneva, IL",
    "St. Charles, IL",
    "Glen Ellyn, IL",
    "Winfield, IL",
  ],
  warren: [
    "Bridgewater, NJ",
    "Watchung, NJ",
    "Bridgewater Township, NJ",
    "Somerville, NJ",
    "Martinsville, NJ",
    "Basking Ridge, NJ",
    "Bedminster, NJ",
    "Long Hill Township, NJ",
    "Far Hills, NJ",
    "Bernardsville, NJ",
  ],
  tempe: [
    "Phoenix, AZ",
    "Chandler, AZ",
    "Gilbert, AZ",
    "Ahwatukee, AZ",
    "Guadalupe, AZ",
    "Sun Lakes, AZ",
    "Paradise Valley, AZ",
    "South Phoenix, AZ",
  ],
  plano: [
    "Frisco, TX",
    "Allen, TX",
    "McKinney, TX",
    "Richardson, TX",
    "Garland, TX",
    "Dallas, TX",
    "Carrollton, TX",
    "Prosper, TX",
    "Wylie, TX",
    "Plano East to Murphy, TX",
  ],
  "the-woodlands": [
    "Conroe, TX",
    "Spring, TX",
    "Montgomery, TX",
    "Huffman, TX",
    "Kingwood, TX",
    "Shenandoah, TX",
    "Magnolia, TX",
    "Willis, TX",
    "Tomball, TX",
    "Cleveland, TX",
  ],
}

/** The live "Nearby Areas We Serve" list for a centre, or null. */
export const nearbyAreasFor = slug => {
  const areas = NEARBY_AREAS[clinicKey(slug)]

  return areas ? { areas, title: "Nearby Areas We Serve" } : null
}

/**
 * Music therapy runs at Carlsbad only. It is named in copy that otherwise
 * renders identically everywhere: two FAQ answers, the shared services card,
 * and three centres' SEO metadata. One rule covers all of them.
 */
/**
 * Music therapy and physical therapy are not offered everywhere, so copy that
 * lists them is adjusted per centre.
 *
 * Physical therapy is Carlsbad only.
 *
 * Music therapy is delivered in person at four California centres, which have
 * music therapists on site. Every other California centre can offer it over
 * telehealth and says so. Centres outside California do not offer it at all.
 *
 * Confirmed by Jensen, September 2026. These are service claims, so treat any
 * change here as needing the same sign-off.
 */
const PHYSICAL_THERAPY_CENTRES = new Set(["carlsbad"])

const MUSIC_THERAPY_ONSITE = new Set([
  "carlsbad",
  "glendale",
  "marin",
  "westlake-village",
])

// Kept in step with the california region in clinicInsurers by hand. Listed
// here rather than derived because that module imports clinicKey from this one.
const CALIFORNIA_CENTRES = new Set([
  "carlsbad",
  "concord",
  "glendale",
  "inland-empire",
  "irvine",
  "laguna-niguel",
  "marin",
  "san-diego",
  "torrance",
  "westlake-village",
])

export const TELEHEALTH_NOTE = "(offered via telehealth)"

/** "onsite", "telehealth", or null where the centre does not offer it. */
export const musicTherapyAt = slug => {
  const key = clinicKey(slug)
  if (MUSIC_THERAPY_ONSITE.has(key)) return "onsite"

  return CALIFORNIA_CENTRES.has(key) ? "telehealth" : null
}

export const offersPhysicalTherapy = slug =>
  PHYSICAL_THERAPY_CENTRES.has(clinicKey(slug))

/**
 * Fit a therapy list to what a centre actually offers.
 *
 * Two list shapes appear in the real content, so both are handled:
 *
 *   "Speech-Language Therapy, Occupational Therapy, Music Therapy"
 *   "speech, occupational, physical, and music therapy"
 *
 * The list is rebuilt rather than having an item deleted, so it still reads as
 * English instead of leaving a dangling comma. Where music therapy is offered
 * by telehealth only, the item is labelled rather than dropped, but only in the
 * first shape: in the second, every item shares one trailing "therapy", so a
 * label would read as if it applied to all of them.
 *
 * Copy that merely mentions either word is left alone. That is why the SEO
 * title "Speech, Occupational, & Music Therapy Services" and a staff member's
 * "Music Therapist" job title survive untouched: an earlier pattern-based
 * attempt turned the first of those into "Speech, Occupational, & Services".
 * The guards below are deliberate and covered by tests.
 */
/**
 * SEO title and description for the three centres whose metadata names music
 * therapy but which do not offer it.
 *
 * All three are outside California, so under the rules above they offer no
 * music therapy at all. The reclassification that gave California three tiers
 * did not change that, so these rewrites still stand.
 *
 * The titles are production's, verbatim. Dev's are stale: Danvers and
 * Naperville still carry an older "Speech, Occupational, & Music Therapy
 * Services" wording, and Danvers' dev description names Dedham rather than
 * Danvers. Production has neither problem.
 *
 * The descriptions are production's with music therapy removed. Production
 * has the same wording, so THIS ONE NEEDS FIXING IN PRODUCTION TOO, not just
 * in dev. Written out rather than edited by pattern, because a regex turned
 * an ampersand list into "Speech, Occupational, & Services".
 *
 * Delete each entry once its Contentful record is corrected.
 */
const SEO_REWRITES = {
  "ma/braintree": {
    description:
      "Unlock your child's full potential with ABA therapy in Braintree, Massachusetts, integrating speech and occupational therapy for a comprehensive treatment approach. Receive an accurate diagnosis for autism, ADHD, and more.",
    title: "Braintree ABA Therapy & Autism Center | Cortica",
  },
  "ma/danvers": {
    description:
      "Unlock your child's full potential at our Danvers ABA therapy center, integrating speech and occupational therapy for a comprehensive treatment approach.",
    title: "Danvers ABA Therapy & Autism Center | Cortica",
  },
  "naperville-il": {
    description:
      "Enhance your child's development with expert ABA therapy in Naperville, Illinois, featuring speech and occupational therapy to address diverse needs for children with autism and other neurodevelopmental differences.",
    title: "Naperville ABA Therapy & Autism Center | Cortica",
  },
}

// Also raw dev slugs: production's titles are already correct. Its
// descriptions still name music therapy, but that is a Contentful fix in
// master, not something to patch from here.
export const seoRewriteFor = slug => SEO_REWRITES[slug] || null

const THERAPY_ITEM = /therap(?:y|ies)\s*$/i
const NAMED_THERAPY = /^(?:and\s+)?(physical|music)\s+therap(?:y|ies)$/i
const THERAPY_LIST = /((?:[\w-]+,\s*)+(?:and\s+)?[\w-]+)\s+(therapy|therapies)/i

const rejoinList = items =>
  items.length === 1
    ? items[0]
    : `${items.slice(0, -1).join(", ")}${items.length > 2 ? "," : ""} and ${
        items[items.length - 1]
      }`

/** "A Therapy, B Therapy, Music Therapy" -> drop or label the last one. */
const adjustWholeItems = (text, keep, label) => {
  const parts = text.split(/,\s*/)
  if (parts.length < 2 || !parts.every(part => THERAPY_ITEM.test(part))) {
    return null
  }

  let touched = false
  const next = []
  for (const part of parts) {
    const named = part.trim().match(NAMED_THERAPY)
    if (!named) {
      next.push(part)
      continue
    }

    touched = true
    if (!keep(named[1].toLowerCase())) continue
    const note = label(named[1].toLowerCase())
    next.push(note ? `${part} ${note}` : part)
  }

  return touched && next.length ? next.join(", ") : null
}

/** "a, b, physical, and music therapy" -> rebuild without what is not offered. */
const adjustList = (text, keep) => {
  const match = text.match(THERAPY_LIST)
  if (!match) return null

  const items = match[1]
    .split(/,\s*(?:and\s+)?|\s+and\s+/)
    .map(item => item.trim())
    .filter(Boolean)
  const kept = items.filter(item => keep(item.toLowerCase()))

  // Nothing to drop, or dropping everything, means this is not the list we
  // thought it was. Leave the copy exactly as the editor wrote it.
  if (kept.length === items.length || kept.length === 0) return null

  const rebuilt = text.replace(match[0], `${rejoinList(kept)} ${match[2]}`)

  // The dropped word may have started the sentence, as in "Physical,
  // occupational, and music therapies".
  return /^[A-Z]/.test(text) && /^[a-z]/.test(rebuilt)
    ? rebuilt[0].toUpperCase() + rebuilt.slice(1)
    : rebuilt
}

export const adjustTherapyList = (text, slug) => {
  if (!text || !/\b(physical|music)\b/i.test(text)) return text

  const music = musicTherapyAt(slug)
  const physical = offersPhysicalTherapy(slug)
  const keep = word =>
    word === "physical" ? physical : word === "music" ? Boolean(music) : true
  // Only the first list shape can carry the note without becoming ambiguous.
  const label = word =>
    word === "music" && music === "telehealth" ? TELEHEALTH_NOTE : ""

  return adjustWholeItems(text, keep, label) ?? adjustList(text, keep) ?? text
}

const STATE_CODES = new Set([
  "ak",
  "al",
  "ar",
  "az",
  "ca",
  "co",
  "ct",
  "dc",
  "de",
  "fl",
  "ga",
  "hi",
  "ia",
  "id",
  "il",
  "in",
  "ks",
  "ky",
  "la",
  "ma",
  "md",
  "me",
  "mi",
  "mn",
  "mo",
  "ms",
  "mt",
  "nc",
  "nd",
  "ne",
  "nh",
  "nj",
  "nm",
  "nv",
  "ny",
  "oh",
  "ok",
  "or",
  "pa",
  "ri",
  "sc",
  "sd",
  "tn",
  "tx",
  "ut",
  "va",
  "vt",
  "wa",
  "wi",
  "wv",
  "wy",
])

export const clinicKey = slug => {
  if (!slug) return ""

  // The town is the last path segment, so "ca/irvine/laguna-niguel" keys on
  // laguna-niguel rather than on irvine.
  const parts = String(slug)
    .toLowerCase()
    .split("/")
    .filter(Boolean)
    .pop()
    .replace(/-autism-therapy$/, "")
    .split("-")

  if (parts.length > 1 && STATE_CODES.has(parts[parts.length - 1])) parts.pop()

  return parts.join("-")
}

/**
 * Photo and team repairs, keyed on the raw dev slug ON PURPOSE.
 *
 * Unlike the rules above, these must NOT follow the content to production.
 * They exist because dev's entries are wrong: Concord showing a photo of
 * Carlsbad, Warren showing Laguna Niguel, teams listing people who left.
 * Production already has the right photos and the right people, so matching
 * there would replace correct content with stock.
 *
 * Deliberately not normalised through clinicKey. Do not "fix" that.
 */
export const overridesFor = slug => CLINIC_OVERRIDES[slug] || {}

/**
 * Look an override's asset up in the assets the page fetched.
 *
 * Matches the asset title first. Some Contentful assets share a title, so the
 * key can instead be part of the file name, which is unique.
 */
export const findAsset = (assets, key) => {
  if (!key) return undefined
  const list = assets || []

  return (
    list.find(asset => asset?.title === key) ||
    list.find(asset => (asset?.file?.url || "").includes(key))
  )
}

/**
 * Contentful stores names with their credentials attached, as in "Sailaja
 * Golla, MD", so compare on the surname alone.
 */
const surnameOf = name =>
  (name || "")
    .replace(/,.*$/, "")
    .trim()
    .split(/[\s-]+/)
    .slice(-1)[0]
    .toLowerCase()

/** Drop the named people from a team list. */
export const applyTeamExclusions = (doctors, exclude) => {
  if (!exclude?.length) return doctors

  const excluded = new Set(exclude.map(surnameOf))

  return (doctors || []).filter(doctor => !excluded.has(surnameOf(doctor.name)))
}

export default CLINIC_OVERRIDES
